import type { Ref } from 'vue';
import { v } from 'h3-valibot';
import type { GlobalSearchResponseBody } from '~/server/api/algolia/search.post';
import { extractError } from '~/utils/extractError';
import { DEFAULT_FETCH_OPTIONS } from '~/utils/defaultFetchOptions';
import { useLogger } from '~/composables/useLogger';
import { useLocale } from '~/composables/useLocale';

export const configurationSchema = v.object({
  limit: v.optional(v.number(), 9),
  sortOrder: v.optional(
    v.object({
      products: v.optional(v.number(), 9),
      contentPages: v.optional(v.number(), 2),
      documents: v.optional(v.number(), 3)
    }),
    {
      products: 1,
      contentPages: 2,
      documents: 3
    }
  )
});

function createCacheKey(query: string, type: string | null) {
  return `${query}-${type}`;
}

export type UseGlobalSearchOptions = Partial<
  v.InferOutput<typeof configurationSchema>
>;

export function useGlobalSearch(configurationRef: Ref<UseGlobalSearchOptions>) {
  const logger = useLogger();
  const locale = useLocale();
  const router = useRouter();
  const route = useRoute();
  const configuration = computed(() =>
    v.parse(configurationSchema, configurationRef ?? {})
  );
  const indexName = ref<string>('');
  const queryId = ref<string>('');

  const responseCache = ref<Record<string, GlobalSearchResponseBody>>({});
  const loading = ref(false);
  const loadingMore = ref(false);
  const error = ref<string | null>(null);
  const searchResponse = ref<GlobalSearchResponseBody | null>(null);
  const searchQuery = ref(route.query.q?.toString() ?? '');
  const autocompleteQuery = ref(route.query.q?.toString() ?? '');
  const searchInputValue = ref(searchQuery.value);
  const activeType = ref<string | null>(route.query.type?.toString() ?? null);
  const abortControllerRef = ref<AbortController | null>(null);

  const hasResults = computed(() => {
    return (
      searchResponse.value != null &&
      searchResponse.value.results['contentPages'].length > 0
    );
  });
  const currentResults = computed(() => searchResponse.value?.results ?? []);
  const hasMoreResults = computed(
    () => searchResponse.value?.hasMoreResults ?? false
  );

  function handleSubmit() {
    router.push({
      query: {
        q: searchInputValue.value
      }
    });
  }

  async function fetchResults(query: string, type: Array<string> | null) {
    const trimmedQuery = query?.trim();
    if (!trimmedQuery) {
      return;
    }

    const cacheKey = createCacheKey(
      trimmedQuery,
      Array.isArray(type) ? type.join(',') : type
    );
    if (responseCache.value[cacheKey]) {
      searchResponse.value = responseCache.value[cacheKey];

      return;
    }

    loading.value = true;

    if (!abortControllerRef.value?.signal.aborted) {
      abortControllerRef.value?.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.value = abortController;

    try {
      searchResponse.value = await $fetch<GlobalSearchResponseBody>(
        '/api/algolia/search',
        {
          ...DEFAULT_FETCH_OPTIONS,
          method: 'POST',
          body: {
            query: trimmedQuery,
            type,
            hitsPerPage: configuration.value.limit,
            locale: locale.value
          },
          signal: abortController.signal
        }
      );
      indexName.value = searchResponse.value?.indexName ?? '';
      queryId.value = searchResponse.value?.queryId ?? '';
      responseCache.value[cacheKey] = searchResponse.value;
      error.value = null;
    } catch (e) {
      if (!abortController.signal.aborted) {
        error.value = extractError(e) ?? 'Error while fetching search results.';
      }
    } finally {
      loading.value = false;
    }

    // TODO ?
    // const firstResult = searchResponse.value?.results[0];
    // // open document in current tab
    // const shouldOpenDocument =
    //   searchResponse.value?.results.length === 1 &&
    //   isDownloadEntryModel(firstResult);
    //
    // if (shouldOpenDocument) {
    //   if (link) {
    //     window.location.href = `#download=${firstResult.id}`;
    //   }
    // }
  }

  async function fetchMoreResults() {
    const cacheKey = createCacheKey(searchQuery.value, activeType.value);
    const cachedResponse = responseCache.value[cacheKey];
    if (!cachedResponse) {
      logger.warn(
        'useGlobalSearch',
        'no response cached - can not load more results'
      );

      return;
    }

    loadingMore.value = true;
    try {
      const response = await $fetch<GlobalSearchResponseBody>(
        '/api/algolia/search',
        {
          ...DEFAULT_FETCH_OPTIONS,
          method: 'POST',
          body: {
            query: searchQuery.value,
            type: activeType.value,
            hitsPerPage: cachedResponse.hitsPerPage,
            offset: cachedResponse.nextOffset,
            sortOrder: configuration.value.sortOrder,
            locale: locale.value
          }
        }
      );
      cachedResponse.nextOffset = response.nextOffset;
      cachedResponse.hasMoreResults = response.hasMoreResults;
      // add results to existing response
      // cachedResponse.results.push(...response.results);

      error.value = null;
    } catch (e) {
      error.value = extractError(e) ?? 'Error while fetching search results.';
    } finally {
      loadingMore.value = false;
    }
  }

  function handleContentTagChange(type: string) {
    const query = {
      ...route.query
    };

    if (activeType.value === type) {
      delete query.type;
    } else {
      query.type = type;
    }

    return router.push({
      query
    });
  }

  function handleLoadMore() {
    if (loadingMore.value) {
      return;
    }

    return fetchMoreResults();
  }

  watch(autocompleteQuery, (newValue) => {
    if (searchInputValue.value !== newValue) {
      searchInputValue.value = newValue;
    }
  });

  return {
    handleSubmit,
    handleLoadMore,
    handleContentTagChange,
    fetchResults,
    indexName,
    queryId,
    searchQuery,
    autocompleteQuery,
    searchInputValue,
    searchResponse,
    activeType,
    currentResults,
    hasMoreResults,
    hasResults,
    loading,
    loadingMore,
    error,
    responseCache
  };
}
